import type { TLessonBase } from '@onesource/schemas';
import { LessonStatus } from '@onesource/schemas';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import {
  useGetLessons,
  useGetSpace,
  useGetModuleProgress,
  useGetSubLessons,
  useGetCategories,
} from '@onesource/services';
import { createFileRoute, Link } from '@tanstack/react-router';
import DOMPurify from 'dompurify';
import { z } from 'zod';
import { ModuleSectionCard } from '../../../../../../../features/community-hub/components/module-section-card';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import { useTheme } from '@mui/material';
import { hasHTML } from '#/utils/has-html';

/**
 * Module Detail Route
 * Path: /community-hub/spaces/:id/modules/:moduleId
 * Shows detailed view of a specific module (CONTENT sub-space)
 * Redesigned based on Figma with accordion sections
 */
export const Route = createFileRoute('/_protected/community-hub/spaces/$id/modules/$moduleId/')({
  validateSearch: z.object({
    sectionId: z.string().optional().catch(undefined),
  }),
  component: ModuleDetailRoute,
});

/**
 * Section Data Loader Component
 * Loads sub-lessons and categories for a section
 */
interface SectionLoaderProps {
  section: TLessonBase;
  index: number;
  parentSpaceId: string;
  moduleId: string;
  expandedSectionId?: string;
  showSectionLabel?: boolean;
  showLessonLabel?: boolean;
}

const SectionLoader = ({
  section,
  index,
  parentSpaceId,
  moduleId,
  expandedSectionId,
  showSectionLabel,
  showLessonLabel,
}: SectionLoaderProps) => {
  const { data: subLessons, isLoading: isLoadingSubLessons } = useGetSubLessons(section.id);
  const { data: categories, isLoading: isLoadingCategories } = useGetCategories(section.id);
  const isLoading = isLoadingSubLessons || isLoadingCategories;

  // Filter to only published sub-lessons
  const publishedSubLessons = subLessons?.filter(l => l.status === LessonStatus.PUBLISHED) || [];

  return (
    <ModuleSectionCard
      section={section}
      index={index}
      subLessons={isLoading ? [] : publishedSubLessons}
      categories={isLoading ? [] : categories || []}
      parentSpaceId={parentSpaceId}
      moduleId={moduleId}
      expandedSectionId={expandedSectionId}
      showSectionLabel={showSectionLabel}
      showLessonLabel={showLessonLabel}
    />
  );
};

function ModuleDetailRoute() {
  const { id: parentSpaceId, moduleId } = Route.useParams();
  const { sectionId: expandedSectionId } = Route.useSearch();
  const theme = useTheme();
  const { data: parentSpace, isLoading: isLoadingParent, error: parentError } = useGetSpace(parentSpaceId);
  const { data: module, isLoading: isLoadingModule, error: moduleError } = useGetSpace(moduleId);
  const { data: lessons, isLoading: isLoadingLessons } = useGetLessons(moduleId);
  const { data: moduleProgress } = useGetModuleProgress(moduleId);

  const isLoading = isLoadingParent || isLoadingModule || isLoadingLessons;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (parentError || moduleError) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load module: {parentError?.message || moduleError?.message}</Alert>
      </Box>
    );
  }

  if (!parentSpace || !module) {
    return (
      <Box p={3}>
        <Alert severity="error">Module not found</Alert>
      </Box>
    );
  }

  const progressPercent = moduleProgress?.progressPercent ?? 0;

  // Filter to only published lessons (sections)
  const publishedSections = lessons?.filter(l => l.status === LessonStatus.PUBLISHED) || [];

  // Sanitize description HTML
  const sanitizedDescription = module.description ? DOMPurify.sanitize(module.description) : '';

  return (
    <Box sx={{ minHeight: 'calc(100vh - 140px)', overflow: 'auto' }}>
      {/* Header Section */}
      <Box sx={{ pb: 2 }}>
        {/* Breadcrumb */}
        <Breadcrumbs
          sx={{
            mb: 2,
            fontFamily: '"Figtree", sans-serif',
            fontSize: 16,
            lineHeight: '22px',
            fontWeight: 400,
            color: theme => theme.palette.grey[600],
            '& .MuiBreadcrumbs-separator': {
              mx: 0.6,
            },
          }}
        >
          <Link
            to="/community-hub/spaces/$id"
            params={{ id: parentSpaceId }}
            style={{ color: theme.palette.grey[600], textDecoration: 'none' }}
          >
            <Typography
              variant="body2"
              sx={{ mt: hasHTML(parentSpace.name) ? -0.5 : 0, fontSize: 16, color: theme.palette.grey[600] }}
            >
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(parentSpace.name) }} />
            </Typography>
          </Link>
          {' / '}
          <Box component="span" sx={{ fontWeight: 700 }}>
            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(module.name) }} />
          </Box>
        </Breadcrumbs>

        {module.showProgressBar !== false && (
          <Box
            sx={{
              mb: 3,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              maxWidth: { xs: '100%', md: 'calc(100% - 205px)' },
            }}
          >
            {/* Progress Percentage */}
            <Typography
              sx={{
                fontFamily: '"Fraunces", serif',
                fontWeight: 400,
                fontSize: 16.6,
                lineHeight: '24px',
                color: '#2e2e2d',
              }}
            >
              {progressPercent}%
            </Typography>

            {/* Progress Bar */}
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{
                width: '100%',
                height: 10,
                borderRadius: 3,
                bgcolor: 'rgba(0, 0, 0, 0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  bgcolor: '#c45569',
                },
              }}
            />
          </Box>
        )}

        {/* Description */}
        {sanitizedDescription && (
          <Box
            sx={{
              maxWidth: { xs: '100%', md: 'calc(100% - 315px)' },
              fontFamily: '"Figtree", sans-serif',
              fontWeight: 400,
              fontSize: 14,
              lineHeight: '19px',
              color: 'black',
              mb: { xs: 2, md: 3 },
              '& strong, & b': {
                fontWeight: 700,
              },
              '& p': {
                margin: 0,
                marginBottom: '8px',
                '&:last-child': {
                  marginBottom: 0,
                },
              },
              '& ol': { listStyle: 'revert', pl: { xs: 3, md: 5 }, margin: 0 },
              '& ul': { listStyle: 'revert', pl: { xs: 3, md: 5 } },
            }}
            dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
          />
        )}
      </Box>

      {/* Sections (Accordion Cards) */}
      <Box
        sx={{
          height: { xs: '100%', md: 'calc(100vh - 390px)' },
          maxWidth: { xs: '100%', md: 'calc(100% - 205px)' },
          overflowY: 'auto',
          scrollbarWidth: 'none',
        }}
      >
        {publishedSections.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '14px', md: '17px' } }}>
            {publishedSections.map((section, index) => (
              <SectionLoader
                key={section.id}
                section={section}
                index={index + 1}
                parentSpaceId={parentSpaceId}
                moduleId={moduleId}
                expandedSectionId={expandedSectionId}
                showSectionLabel={module.showSectionLabel !== false}
                showLessonLabel={module.showLessonLabel !== false}
              />
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              bgcolor: 'white',
              borderRadius: '10px',
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Figtree", sans-serif',
                fontWeight: 400,
                fontSize: 14,
                color: 'rgba(0, 0, 0, 0.6)',
              }}
            >
              No sections available in this module.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
